import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  type TooltipItem,
} from 'chart.js';
import { Line, Doughnut, Pie } from 'react-chartjs-2';
import {
  ArrowPathIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronDownIcon,
  ClockIcon,
  InformationCircleIcon,
  MagnifyingGlassMinusIcon,
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import { useGlobalMonitoringAlerts } from '../../contexts/GlobalMonitoringAlertContext';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ArcElement, Tooltip);

type Point = { label: string; value: number; at?: string };

interface LiveMonitoringSnapshot {
  configured: boolean;
  liveAzureEnabled: boolean;
  range: string;
  fetchedAt: string;
  postgresConfigured: boolean;
  logAnalyticsConfigured: boolean;
  postgres: {
    cpuPercent: Point[];
    memoryPercent: Point[];
    storageUsedGb: number | null;
    storageFreeGb: number | null;
    storageUsedPercent: number | null;
    storageUsedPercentSeries: Point[];
    storageTotalGb: number | null;
    storageUsedGbSeries: Point[];
    storageFreeGbSeries: Point[];
    databaseSizes: {
      databaseName: string;
      sizeBytes: number;
    }[];
  };
  application: {
    cpuLoadPercent: Point[];
    memoryUsagePercent: Point[];
    networkThroughputBytesPerSec: Point[];
    networkLoadPercent: Point[];
    httpRequestsPerSec: Point[];
  };
  errors: { source: string; message: string }[];
  applicationInsightsPublishing?: boolean;
  setupHints?: string[];
}

const EMPTY_LIVE_POSTGRES: LiveMonitoringSnapshot['postgres'] = {
  cpuPercent: [],
  memoryPercent: [],
  storageUsedGb: null,
  storageFreeGb: null,
  storageUsedPercent: null,
  storageUsedPercentSeries: [],
  storageTotalGb: null,
  storageUsedGbSeries: [],
  storageFreeGbSeries: [],
  databaseSizes: [],
};

const EMPTY_LIVE_APPLICATION: LiveMonitoringSnapshot['application'] = {
  cpuLoadPercent: [],
  memoryUsagePercent: [],
  networkThroughputBytesPerSec: [],
  networkLoadPercent: [],
  httpRequestsPerSec: [],
};

interface LiveMonitoringPostgresqlSnapshot {
  configured: boolean;
  liveAzureEnabled: boolean;
  range: string;
  fetchedAt: string;
  postgresConfigured: boolean;
  postgres: LiveMonitoringSnapshot['postgres'];
  errors: { source: string; message: string }[];
  setupHints?: string[];
}

interface LiveMonitoringApplicationSnapshot {
  configured: boolean;
  liveAzureEnabled: boolean;
  range: string;
  fetchedAt: string;
  logAnalyticsConfigured: boolean;
  application: LiveMonitoringSnapshot['application'];
  errors: { source: string; message: string }[];
  applicationInsightsPublishing?: boolean;
  setupHints?: string[];
}

function mergeSetupHints(a?: string[], b?: string[]): string[] {
  return [...new Set([...(a ?? []), ...(b ?? [])])];
}

function latestFetchedAt(a?: string, b?: string): string {
  if (!a) return b ?? '';
  if (!b) return a;
  return new Date(a).getTime() >= new Date(b).getTime() ? a : b;
}

/** Buckets per line chart (demo mode time axis resolution). */
const LINE_POINT_COUNT = 48;

const TIME_RANGES = [
  { id: '5m', label: '5m', displayLabel: 'Last 5 minutes', windowMs: 5 * 60 * 1000 },
  { id: '10m', label: '10m', displayLabel: 'Last 10 minutes', windowMs: 10 * 60 * 1000 },
  { id: '15m', label: '15m', displayLabel: 'Last 15 minutes', windowMs: 15 * 60 * 1000 },
  { id: '30m', label: '30m', displayLabel: 'Last 30 minutes', windowMs: 30 * 60 * 1000 },
  { id: '1h', label: '1h', displayLabel: 'Last 1 hour', windowMs: 60 * 60 * 1000 },
  { id: '2h', label: '2h', displayLabel: 'Last 2 hours', windowMs: 2 * 60 * 60 * 1000 },
  { id: '6h', label: '6h', displayLabel: 'Last 6 hours', windowMs: 6 * 60 * 60 * 1000 },
  { id: '12h', label: '12h', displayLabel: 'Last 12 hours', windowMs: 12 * 60 * 60 * 1000 },
  { id: '24h', label: '24h', displayLabel: 'Last 24 hours', windowMs: 24 * 60 * 60 * 1000 },
  { id: '7d', label: '7d', displayLabel: 'Last 7 days', windowMs: 7 * 24 * 60 * 60 * 1000 },
  { id: '30d', label: '30d', displayLabel: 'Last 30 days', windowMs: 30 * 24 * 60 * 60 * 1000 },
] as const;

type TimeRangeId = (typeof TIME_RANGES)[number]['id'];

/** Refresh dropdown options while on the Monitoring page (`ms` used for live API polling, except `off` / `auto`). */
const REFRESH_OPTIONS = [
  { id: 'off', label: 'Off', ms: null as number | null },
  /** On-page default when the user does not pick a fixed cadence (matches background poller). */
  { id: 'auto', label: 'Auto', ms: null },
  { id: '5s', label: '5s', ms: 5_000 },
  { id: '10s', label: '10s', ms: 10_000 },
  { id: '30s', label: '30s', ms: 30_000 },
  { id: '1m', label: '1m', ms: 60_000 },
  { id: '5m', label: '5m', ms: 5 * 60_000 },
  { id: '15m', label: '15m', ms: 15 * 60_000 },
  { id: '30m', label: '30m', ms: 30 * 60_000 },
  { id: '1h', label: '1h', ms: 60 * 60_000 },
  { id: '2h', label: '2h', ms: 2 * 60 * 60_000 },
  { id: '1d', label: '1d', ms: 24 * 60 * 60_000 },
] as const;

type RefreshIntervalId = (typeof REFRESH_OPTIONS)[number]['id'];

/** Maps the Monitoring page refresh dropdown to React Query `refetchInterval` (false = off). */
function getMonitoringPageRefetchInterval(
  refreshIntervalId: RefreshIntervalId,
  configuredAutoPollMs: number
): number | false {
  if (refreshIntervalId === 'off') return false;
  if (refreshIntervalId === 'auto') return configuredAutoPollMs;
  const opt = REFRESH_OPTIONS.find((r) => r.id === refreshIntervalId);
  return opt?.ms ?? configuredAutoPollMs;
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

/** Display numbers with exactly two decimal places (e.g. 4.15). */
const fmt2 = (n: number) => (Number.isFinite(n) ? n.toFixed(2) : '0.00');

const formatStorageBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  if (bytes >= 1_000_000_000) return `${fmt2(bytes / 1_000_000_000)} GB`;
  if (bytes >= 1_000_000) return `${fmt2(bytes / 1_000_000)} MB`;
  if (bytes >= 1_000) return `${fmt2(bytes / 1_000)} KB`;
  return `${Math.round(bytes)} B`;
};

const nextValue = (current: number, delta: number, min = 0, max = 100) =>
  clamp(current + (Math.random() * 2 - 1) * delta, min, max);

function formatTimeLabel(date: Date, windowMs: number): string {
  if (windowMs <= 10 * 60 * 1000) {
    return date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  }
  if (windowMs <= 24 * 60 * 60 * 1000) {
    return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
  }
  if (windowMs <= 7 * 24 * 60 * 60 * 1000) {
    return `${date.getMonth() + 1}/${date.getDate()} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  }
  return `${date.getMonth() + 1}/${date.getDate()}`;
}

function makeLabelsForRange(pointCount: number, windowMs: number, endMs: number = Date.now()): string[] {
  if (pointCount <= 0) return [];
  if (pointCount === 1) {
    return [formatTimeLabel(new Date(endMs), windowMs)];
  }
  const startMs = endMs - windowMs;
  return Array.from({ length: pointCount }, (_, i) => {
    const t = startMs + (i / (pointCount - 1)) * windowMs;
    return formatTimeLabel(new Date(t), windowMs);
  });
}

function seedSeries(count: number, min: number, max: number, volatility: number): number[] {
  const out: number[] = [];
  let v = min + Math.random() * (max - min);
  for (let i = 0; i < count; i++) {
    v = nextValue(v, volatility, min, max);
    out.push(v);
  }
  return out;
}

const toSeries = (labels: string[], values: number[]): Point[] =>
  labels.map((label, index) => ({ label, value: values[index] ?? 0 }));

/** Y-axis bounds from series values so the chart zooms to the active range (with padding). */
function yRangeFromPoints(points: Point[], isPercentUnit: boolean): { min: number; max: number } {
  const values = points.map((p) => p.value).filter((v) => Number.isFinite(v));
  if (!values.length) return { min: 0, max: 1 };

  const minV = Math.min(...values);
  const maxV = Math.max(...values);
  const span = maxV - minV;
  const padRatio = 0.08;

  const padFlat = () => {
    const base = Math.max(Math.abs(minV), Math.abs(maxV), isPercentUnit ? 5 : 1);
    return isPercentUnit ? Math.max(2, base * 0.05) : Math.max(base * 0.08, 0.001);
  };

  if (span <= 1e-12) {
    const half = padFlat();
    let min = minV - half;
    let max = maxV + half;
    if (isPercentUnit) {
      min = clamp(min, 0, 100);
      max = clamp(max, 0, 100);
    } else if (minV >= 0) {
      min = Math.max(0, min);
    }
    if (max <= min) max = min + (isPercentUnit ? 1 : Math.max(min * 0.02, 0.01));
    return { min, max };
  }

  const pad = Math.max(span * padRatio, isPercentUnit ? 0.5 : span * 0.02 || 1e-6);
  let min = minV - pad;
  let max = maxV + pad;

  if (isPercentUnit) {
    min = clamp(min, 0, 100);
    max = clamp(max, 0, 100);
  } else if (minV >= 0) {
    min = Math.max(0, min);
  }

  if (max <= min) {
    max = min + (isPercentUnit ? 1 : Math.max(Math.abs(min) * 0.05, 0.01));
  }

  return { min, max };
}

const lineOptionsDualStorageGb = (yRange: { min: number; max: number }) => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index' as const, intersect: false },
  plugins: {
    legend: {
      display: true,
      position: 'bottom' as const,
      labels: { boxWidth: 12, usePointStyle: true, font: { size: 11 }, color: '#374151' },
    },
    datalabels: { display: false },
    tooltip: {
      enabled: true,
      callbacks: {
        title: (items: TooltipItem<'line'>[]) => {
          if (!items.length) return '';
          const idx = items[0].dataIndex;
          const label = items[0].chart.data.labels?.[idx];
          return typeof label === 'string' ? label : '';
        },
        label: (ctx: TooltipItem<'line'>) => {
          const name = String(ctx.dataset.label ?? '');
          const v = ctx.parsed.y as number;
          return `${name}: ${fmt2(v)} GB`;
        },
      },
    },
  },
  scales: {
    x: {
      type: 'category' as const,
      display: true,
      title: {
        display: true,
        text: 'Time',
        color: '#374151',
        font: { size: 11, weight: 'bold' as const },
      },
      grid: {
        display: true,
        color: 'rgba(0, 0, 0, 0.06)',
        drawBorder: true,
        borderColor: '#e5e7eb',
      },
      ticks: {
        maxRotation: 45,
        minRotation: 0,
        autoSkip: true,
        maxTicksLimit: 10,
        color: '#6b7280',
        font: { size: 10 },
      },
    },
    y: {
      type: 'linear' as const,
      display: true,
      beginAtZero: false,
      min: yRange.min,
      max: yRange.max,
      title: {
        display: true,
        text: 'Storage (GB)',
        color: '#374151',
        font: { size: 11, weight: 'bold' as const },
      },
      grid: { color: 'rgba(0, 0, 0, 0.06)', drawBorder: false },
      ticks: {
        color: '#6b7280',
        font: { size: 10 },
        maxTicksLimit: 8,
        callback: (tickValue: string | number) => {
          const v = typeof tickValue === 'number' ? tickValue : Number(tickValue);
          if (Number.isNaN(v)) return String(tickValue);
          return fmt2(v);
        },
      },
    },
  },
});

const lineOptions = (yRange: { min: number; max: number }, valueUnit = '%') => {
  const isPercent = valueUnit.trim() === '%';
  const yAxisTitle = isPercent ? 'Percent (%)' : valueUnit.includes('req/s') ? 'Requests/sec' : 'Value';

  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index' as const, intersect: false },
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
      tooltip: {
        enabled: true,
        callbacks: {
          title: (items: TooltipItem<'line'>[]) => {
            if (!items.length) return '';
            const idx = items[0].dataIndex;
            const label = items[0].chart.data.labels?.[idx];
            return typeof label === 'string' ? label : '';
          },
          label: (ctx: TooltipItem<'line'>) => {
            const v = ctx.parsed.y as number;
            return `${fmt2(v)}${valueUnit}`;
          },
        },
      },
    },
    scales: {
      x: {
        type: 'category' as const,
        display: true,
        title: {
          display: true,
          text: 'Time',
          color: '#374151',
          font: { size: 11, weight: 'bold' as const },
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.06)',
          drawBorder: true,
          borderColor: '#e5e7eb',
        },
        ticks: {
          maxRotation: 45,
          minRotation: 0,
          autoSkip: true,
          maxTicksLimit: 10,
          color: '#6b7280',
          font: { size: 10 },
        },
      },
      y: {
        type: 'linear' as const,
        display: true,
        beginAtZero: false,
        min: yRange.min,
        max: yRange.max,
        title: {
          display: true,
          text: yAxisTitle,
          color: '#374151',
          font: { size: 11, weight: 'bold' as const },
        },
        grid: { color: 'rgba(0, 0, 0, 0.06)', drawBorder: false },
        ticks: {
          color: '#6b7280',
          font: { size: 10 },
          maxTicksLimit: 8,
          callback: (tickValue: string | number) => {
            const v = typeof tickValue === 'number' ? tickValue : Number(tickValue);
            if (Number.isNaN(v)) return String(tickValue);
            if (isPercent) return `${fmt2(v)}%`;
            return fmt2(v);
          },
        },
      },
    },
  };
};

const GaugeCard: React.FC<{
  title: string;
  value: number;
  total: number;
  unit?: string;
  reverse?: boolean;
  warning?: number;
  critical?: number;
  loading?: boolean;
}> = ({ title, value, total, unit = '', reverse = false, warning = 70, critical = 85, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">{title}</h3>
        <div className="relative h-32 flex items-center justify-center text-sm text-gray-500">Loading…</div>
      </div>
    );
  }

  const percent = total > 0 ? clamp((value / total) * 100, 0, 100) : 0;
  const effective = reverse ? 100 - percent : percent;
  const color =
    effective >= critical ? 'rgba(239, 68, 68, 0.9)' : effective >= warning ? 'rgba(245, 158, 11, 0.9)' : 'rgba(16, 185, 129, 0.9)';

  const data = {
    datasets: [
      {
        data: [percent, 100 - percent],
        backgroundColor: [color, 'rgba(229, 231, 235, 1)'],
        borderWidth: 0,
        circumference: 180,
        rotation: 270,
        cutout: '75%',
      },
    ],
  };

  const safeValue = Number.isFinite(value) ? value : 0;
  const isPercentOnly = unit === '%' && total === 100;

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-2">{title}</h3>
      <div className="relative h-32">
        <Doughnut
          data={data}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              datalabels: { display: false },
              tooltip: {
                enabled: true,
                filter: (item) => item.dataIndex === 0,
                callbacks: {
                  label: () =>
                    isPercentOnly
                      ? `${fmt2(safeValue)}%`
                      : `${fmt2(safeValue)}${unit} (${fmt2(percent)}% of total)`,
                },
              },
            },
          }}
        />
        <div
          className="absolute inset-0 flex flex-col items-center justify-end pb-2 pointer-events-none"
          aria-hidden
        >
          <span className="text-lg font-semibold text-gray-900 tabular-nums leading-tight">
            {isPercentOnly ? `${fmt2(safeValue)}%` : `${fmt2(safeValue)}${unit}`}
          </span>
          {!isPercentOnly && (
            <span className="text-xs text-gray-500 tabular-nums mt-0.5">{fmt2(percent)}% of total</span>
          )}
        </div>
      </div>
    </div>
  );
};

const DB_SIZE_COLORS = [
  '#7DBF62',
  '#F6C10F',
  '#2F80ED',
  '#F97316',
  '#DC2626',
  '#A855F7',
  '#166534',
  '#6B7280',
] as const;

const DatabaseSizePieCard: React.FC<{
  entries: { databaseName: string; sizeBytes: number }[];
  loading?: boolean;
}> = ({ entries, loading = false }) => {
  const [hiddenNames, setHiddenNames] = useState<Set<string>>(() => new Set());

  const displayEntries = useMemo(() => {
    if (entries.length <= 8) return entries;
    const primary = entries.slice(0, 7);
    const remainder = entries.slice(7).reduce((sum, item) => sum + item.sizeBytes, 0);
    return [...primary, { databaseName: 'others', sizeBytes: remainder }];
  }, [entries]);

  const displayEntriesKey = useMemo(
    () => displayEntries.map((e) => `${e.databaseName}:${e.sizeBytes}`).join('\0'),
    [displayEntries]
  );
  const prevDisplayKey = useRef(displayEntriesKey);
  useEffect(() => {
    if (prevDisplayKey.current !== displayEntriesKey) {
      prevDisplayKey.current = displayEntriesKey;
      setHiddenNames(new Set());
    }
  }, [displayEntriesKey]);

  const visibleEntries = useMemo(
    () => displayEntries.filter((e) => !hiddenNames.has(e.databaseName)),
    [displayEntries, hiddenNames]
  );

  const colorForDb = useCallback(
    (databaseName: string) => {
      const index = displayEntries.findIndex((d) => d.databaseName === databaseName);
      return DB_SIZE_COLORS[(index >= 0 ? index : 0) % DB_SIZE_COLORS.length];
    },
    [displayEntries]
  );

  const toggleDbVisibility = useCallback((databaseName: string) => {
    setHiddenNames((prev) => {
      const next = new Set(prev);
      if (next.has(databaseName)) {
        next.delete(databaseName);
        return next;
      }
      next.add(databaseName);
      const stillVisible = displayEntries.filter((e) => !next.has(e.databaseName)).length;
      if (stillVisible === 0) return prev;
      return next;
    });
  }, [displayEntries]);

  const pieData = useMemo(
    () => ({
      labels: visibleEntries.map((entry) => entry.databaseName),
      datasets: [
        {
          data: visibleEntries.map((entry) => entry.sizeBytes),
          backgroundColor: visibleEntries.map((entry) => colorForDb(entry.databaseName)),
          borderWidth: 0,
        },
      ],
    }),
    [visibleEntries, colorForDb]
  );

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-2 mb-4">
          <h3 className="text-lg font-medium text-gray-900">Database Size</h3>
          <InformationCircleIcon className="h-4 w-4 text-gray-400" />
        </div>
        <div className="h-72 flex items-center justify-center text-sm text-gray-500">Loading…</div>
      </div>
    );
  }

  if (!displayEntries.length) {
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-2 mb-4">
          <h3 className="text-lg font-medium text-gray-900">Database Size</h3>
          <InformationCircleIcon className="h-4 w-4 text-gray-400" />
        </div>
        <div className="h-72 flex items-center justify-center text-sm text-gray-500">
          No database size data available
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center gap-2 mb-4">
        <h3 className="text-lg font-medium text-gray-900">Database Size</h3>
        <InformationCircleIcon className="h-4 w-4 text-gray-400" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-center">
        <div className="h-64">
          {visibleEntries.length > 0 ? (
            <Pie
              data={pieData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  datalabels: { display: false },
                  tooltip: {
                    callbacks: {
                      label: (context) => {
                        const label = context.label || '';
                        const value = Number(context.raw ?? 0);
                        return `${label}  Value: ${formatStorageBytes(value)}`;
                      },
                    },
                  },
                },
              }}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-gray-500">
              Select at least one database in the legend
            </div>
          )}
        </div>
        <div className="space-y-2 max-h-64 overflow-auto pr-1" role="list" aria-label="Databases — click to show or hide in chart">
          {displayEntries.map((entry, index) => {
            const hidden = hiddenNames.has(entry.databaseName);
            const swatchColor = DB_SIZE_COLORS[index % DB_SIZE_COLORS.length];
            return (
              <button
                key={entry.databaseName}
                type="button"
                role="listitem"
                onClick={() => toggleDbVisibility(entry.databaseName)}
                className={`flex w-full items-center justify-between gap-2 rounded-md px-1 py-1 text-left text-sm transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                  hidden ? 'opacity-50' : ''
                }`}
                aria-pressed={!hidden}
                aria-label={`${hidden ? 'Show' : 'Hide'} ${entry.databaseName} in chart`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="inline-block h-1 w-4 shrink-0 rounded-full"
                    style={{ backgroundColor: swatchColor, opacity: hidden ? 0.35 : 1 }}
                  />
                  <span className={`truncate ${hidden ? 'text-gray-500 line-through' : 'text-gray-800'}`}>
                    {entry.databaseName}
                  </span>
                </div>
                <span className={`whitespace-nowrap ${hidden ? 'text-gray-400' : 'text-gray-700'}`}>
                  Value: {formatStorageBytes(entry.sizeBytes)}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const PostgresStorageDualLineCard: React.FC<{
  usedPoints: Point[];
  freePoints: Point[];
  loading?: boolean;
}> = ({ usedPoints, freePoints, loading = false }) => {
  const { chartData, yRange } = useMemo(() => {
    const hasU = usedPoints.length > 0;
    const hasF = freePoints.length > 0;
    if (!hasU && !hasF) {
      return { chartData: null as { labels: string[]; datasets: unknown[] } | null, yRange: { min: 0, max: 1 } };
    }
    const lineStyle = {
      backgroundColor: 'transparent' as const,
      borderWidth: 2,
      pointRadius: 0,
      pointHoverRadius: 0,
      pointHitRadius: 8,
      tension: 0.35,
      fill: false as const,
    };
    if (hasU && hasF) {
      const n = Math.min(usedPoints.length, freePoints.length);
      const labels = usedPoints.slice(0, n).map((p) => p.label);
      const u = usedPoints.slice(0, n).map((p) => p.value);
      const f = freePoints.slice(0, n).map((p) => p.value);
      const merged: Point[] = [...usedPoints.slice(0, n), ...freePoints.slice(0, n)];
      const yRange = yRangeFromPoints(merged, false);
      return {
        chartData: {
          labels,
          datasets: [
            {
              label: 'Storage used (GB)',
              data: u,
              borderColor: 'rgb(37, 99, 235)',
              ...lineStyle,
            },
            {
              label: 'Storage free (GB)',
              data: f,
              borderColor: 'rgb(16, 185, 129)',
              ...lineStyle,
            },
          ],
        },
        yRange,
      };
    }
    const single = hasU ? usedPoints : freePoints;
    const labels = single.map((p) => p.label);
    const vals = single.map((p) => p.value);
    const yRange = yRangeFromPoints(single, false);
    return {
      chartData: {
        labels,
        datasets: [
          {
            label: hasU ? 'Storage used (GB)' : 'Storage free (GB)',
            data: vals,
            borderColor: hasU ? 'rgb(37, 99, 235)' : 'rgb(16, 185, 129)',
            ...lineStyle,
          },
        ],
      },
      yRange,
    };
  }, [usedPoints, freePoints]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">PostgreSQL storage (used and free)</h3>
        <div className="h-64 min-h-[16rem] flex items-center justify-center text-sm text-gray-500">Loading…</div>
      </div>
    );
  }

  if (!chartData) {
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">PostgreSQL storage (used and free)</h3>
        <div className="h-64 min-h-[16rem] flex items-center justify-center text-sm text-gray-500">
          No data for this period
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-2">PostgreSQL storage (used and free)</h3>
      <p className="text-xs text-gray-500 mb-2">Decimal gigabytes (10⁹ bytes), aligned with Azure Monitor flexible server metrics.</p>
      <div className="h-64 min-h-[16rem]">
        <Line data={chartData as never} options={lineOptionsDualStorageGb(yRange)} />
      </div>
    </div>
  );
};

const LineCard: React.FC<{
  title: string;
  points: Point[];
  color: string;
  unit?: string;
  loading?: boolean;
}> = ({ title, points, color, unit = '%', loading = false }) => {
  const isPercentUnit = unit.trim() === '%';
  const yRange = useMemo(() => yRangeFromPoints(points, isPercentUnit), [points, isPercentUnit]);

  const data = useMemo(
    () => ({
      labels: points.map((p) => p.label),
      datasets: [
        {
          data: points.map((p) => p.value),
          borderColor: color,
          backgroundColor: 'transparent',
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 0,
          pointHitRadius: 8,
          tension: 0.35,
          fill: false,
        },
      ],
    }),
    [points, color]
  );

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">{title}</h3>
        <div className="h-64 min-h-[16rem] flex items-center justify-center text-sm text-gray-500">
          Loading…
        </div>
      </div>
    );
  }

  if (!points.length) {
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">{title}</h3>
        <div className="h-64 min-h-[16rem] flex items-center justify-center text-sm text-gray-500">
          No data for this period
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-2">{title}</h3>
      <div className="h-64 min-h-[16rem]">
        <Line data={data} options={lineOptions(yRange, unit)} />
      </div>
    </div>
  );
};

async function fetchMonitoringPostgresql(range: TimeRangeId): Promise<LiveMonitoringPostgresqlSnapshot> {
  const res = await api.get<LiveMonitoringPostgresqlSnapshot>('/monitoring/postgresql-data', { params: { range } });
  return res.data;
}

async function fetchMonitoringApplication(range: TimeRangeId): Promise<LiveMonitoringApplicationSnapshot> {
  const res = await api.get<LiveMonitoringApplicationSnapshot>('/monitoring/app-monitoring-data', {
    params: { range },
  });
  return res.data;
}

const MonitoringDashboard: React.FC = () => {
  const { syncMonitoringThresholdAlerts, refreshAlertThresholds, livePollIntervalMs } =
    useGlobalMonitoringAlerts();
  const monitoringThresholdsPrimedRef = useRef(false);

  const [timeRangeId, setTimeRangeId] = useState<TimeRangeId>('1h');
  const [refreshIntervalId, setRefreshIntervalId] = useState<RefreshIntervalId>('30s');
  const rangeConfig = useMemo(
    () => TIME_RANGES.find((r) => r.id === timeRangeId) ?? TIME_RANGES[4],
    [timeRangeId]
  );

  const liveMetricsRefetchInterval = useMemo(
    () => getMonitoringPageRefetchInterval(refreshIntervalId, livePollIntervalMs),
    [refreshIntervalId, livePollIntervalMs]
  );

  const liveMetricsStaleTime = useMemo(() => {
    if (liveMetricsRefetchInterval === false) return livePollIntervalMs;
    return Math.min(liveMetricsRefetchInterval, livePollIntervalMs);
  }, [liveMetricsRefetchInterval, livePollIntervalMs]);

  const [pgQuery, appQuery] = useQueries({
    queries: [
      {
        queryKey: ['monitoring', 'postgresql-data', timeRangeId],
        queryFn: () => fetchMonitoringPostgresql(timeRangeId),
        staleTime: liveMetricsStaleTime,
        gcTime: 60 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchInterval: liveMetricsRefetchInterval,
        retry: 1,
      },
      {
        queryKey: ['monitoring', 'app-monitoring-data', timeRangeId],
        queryFn: () => fetchMonitoringApplication(timeRangeId),
        staleTime: liveMetricsStaleTime,
        gcTime: 60 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchInterval: liveMetricsRefetchInterval,
        retry: 1,
      },
    ],
  });

  const liveData = useMemo((): LiveMonitoringSnapshot | undefined => {
    const pg = pgQuery.data;
    const app = appQuery.data;
    if (!pg && !app) return undefined;
    return {
      configured: Boolean(pg?.configured || app?.configured),
      liveAzureEnabled: Boolean(pg?.liveAzureEnabled || app?.liveAzureEnabled),
      range: pg?.range ?? app?.range ?? timeRangeId,
      fetchedAt: latestFetchedAt(pg?.fetchedAt, app?.fetchedAt),
      postgresConfigured: pg?.postgresConfigured ?? false,
      logAnalyticsConfigured: app?.logAnalyticsConfigured ?? false,
      postgres: pg?.postgres ?? EMPTY_LIVE_POSTGRES,
      application: app?.application ?? EMPTY_LIVE_APPLICATION,
      errors: [...(pg?.errors ?? []), ...(app?.errors ?? [])],
      applicationInsightsPublishing: app?.applicationInsightsPublishing ?? false,
      setupHints: mergeSetupHints(pg?.setupHints, app?.setupHints),
    };
  }, [pgQuery.data, appQuery.data, timeRangeId]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!monitoringThresholdsPrimedRef.current) {
        await refreshAlertThresholds();
        if (cancelled) return;
        monitoringThresholdsPrimedRef.current = true;
      }
      syncMonitoringThresholdAlerts(pgQuery.data, appQuery.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshAlertThresholds, syncMonitoringThresholdAlerts, pgQuery.data, appQuery.data]);

  const isPending =
    (pgQuery.isPending && pgQuery.data === undefined) || (appQuery.isPending && appQuery.data === undefined);

  const isError =
    liveData === undefined &&
    pgQuery.isFetched &&
    appQuery.isFetched &&
    pgQuery.isError &&
    appQuery.isError;

  const error =
    isError && pgQuery.error instanceof Error && appQuery.error instanceof Error
      ? new Error(`${pgQuery.error.message}; ${appQuery.error.message}`)
      : isError
        ? pgQuery.error instanceof Error
          ? pgQuery.error
          : appQuery.error instanceof Error
            ? appQuery.error
            : new Error('Failed to load monitoring data')
        : null;

  const isFetching = pgQuery.isFetching || appQuery.isFetching;

  const refetch = () => Promise.all([pgQuery.refetch(), appQuery.refetch()]);

  const timeRangeIndex = useMemo(() => TIME_RANGES.findIndex((r) => r.id === timeRangeId), [timeRangeId]);
  const canTimeShiftBack = timeRangeIndex > 0;
  const canTimeShiftForward = timeRangeIndex >= 0 && timeRangeIndex < TIME_RANGES.length - 1;

  const shiftTimeRange = (delta: -1 | 1) => {
    const next = timeRangeIndex + delta;
    if (next < 0 || next >= TIME_RANGES.length) return;
    setTimeRangeId(TIME_RANGES[next].id);
  };

  const zoomTimeRangeOut = () => {
    const next = Math.min(TIME_RANGES.length - 1, timeRangeIndex + 2);
    if (next !== timeRangeIndex) setTimeRangeId(TIME_RANGES[next].id);
  };

  const useLive = Boolean(liveData?.liveAzureEnabled && liveData?.configured);
  const useDemo = Boolean(liveData && !useLive);
  /** Only while the first request is in flight — not when the request failed (no stale data). */
  const awaitingSnapshot = isPending && liveData === undefined;

  const tickMs = useMemo(() => {
    const w = rangeConfig.windowMs;
    return clamp(Math.floor(w / LINE_POINT_COUNT), 2000, 60_000);
  }, [rangeConfig.windowMs]);

  const [labels, setLabels] = useState<string[]>([]);
  const [pgCpu, setPgCpu] = useState<number[]>([]);
  const [pgMemory, setPgMemory] = useState<number[]>([]);
  const [storageUsedGb, setStorageUsedGb] = useState<number[]>([]);
  const [appCpu, setAppCpu] = useState<number[]>([]);
  const [appMemory, setAppMemory] = useState<number[]>([]);
  const [networkLoad, setNetworkLoad] = useState<number[]>([]);
  const [appLoad, setAppLoad] = useState<number[]>([]);

  const demoTotalStorageGb = 1024;

  useEffect(() => {
    if (!useDemo || isPending) return;
    const w = rangeConfig.windowMs;
    setLabels(makeLabelsForRange(LINE_POINT_COUNT, w));
    setPgCpu(seedSeries(LINE_POINT_COUNT, 20, 80, 7));
    setPgMemory(seedSeries(LINE_POINT_COUNT, 30, 90, 5));
    setStorageUsedGb(seedSeries(LINE_POINT_COUNT, 350, 950, 15));
    setAppCpu(seedSeries(LINE_POINT_COUNT, 15, 75, 9));
    setAppMemory(seedSeries(LINE_POINT_COUNT, 35, 85, 6));
    setNetworkLoad(seedSeries(LINE_POINT_COUNT, 10, 95, 12));
    setAppLoad(seedSeries(LINE_POINT_COUNT, 5, 350, 25));
  }, [timeRangeId, rangeConfig.windowMs, useDemo, isPending]);

  useEffect(() => {
    if (!useDemo || isPending) return;
    const w = rangeConfig.windowMs;
    const timer = setInterval(() => {
      const end = Date.now();
      setLabels(makeLabelsForRange(LINE_POINT_COUNT, w, end));
      setPgCpu((prev) => [...prev.slice(1), nextValue(prev[prev.length - 1] ?? 40, 7)]);
      setPgMemory((prev) => [...prev.slice(1), nextValue(prev[prev.length - 1] ?? 55, 5)]);
      setStorageUsedGb((prev) => [...prev.slice(1), nextValue(prev[prev.length - 1] ?? 430, 12, 350, 1000)]);
      setAppCpu((prev) => [...prev.slice(1), nextValue(prev[prev.length - 1] ?? 30, 9)]);
      setAppMemory((prev) => [...prev.slice(1), nextValue(prev[prev.length - 1] ?? 55, 6)]);
      setNetworkLoad((prev) => [...prev.slice(1), nextValue(prev[prev.length - 1] ?? 35, 12)]);
      setAppLoad((prev) => [...prev.slice(1), nextValue(prev[prev.length - 1] ?? 16, 18, 0, 400)]);
    }, tickMs);

    return () => clearInterval(timer);
  }, [rangeConfig.windowMs, tickMs, useDemo, isPending]);

  const pgCpuPoints = useMemo(() => {
    if (!liveData) return [];
    if (useLive) return liveData.postgres.cpuPercent;
    return toSeries(labels, pgCpu);
  }, [liveData, useLive, labels, pgCpu]);

  const pgMemoryPoints = useMemo(() => {
    if (!liveData) return [];
    if (useLive) return liveData.postgres.memoryPercent;
    return toSeries(labels, pgMemory);
  }, [liveData, useLive, labels, pgMemory]);

  const appCpuPoints = useMemo(() => {
    if (!liveData) return [];
    if (useLive) return liveData.application.cpuLoadPercent;
    return toSeries(labels, appCpu);
  }, [liveData, useLive, labels, appCpu]);

  const appMemoryPoints = useMemo(() => {
    if (!liveData) return [];
    if (useLive) return liveData.application.memoryUsagePercent;
    return toSeries(labels, appMemory);
  }, [liveData, useLive, labels, appMemory]);

  const networkLoadPoints = useMemo(() => {
    if (!liveData) return [];
    if (useLive) return liveData.application.networkLoadPercent;
    return toSeries(labels, networkLoad);
  }, [liveData, useLive, labels, networkLoad]);

  const appLoadPoints = useMemo(() => {
    if (!liveData) return [];
    if (useLive) return liveData.application.httpRequestsPerSec;
    return toSeries(labels, appLoad);
  }, [liveData, useLive, labels, appLoad]);

  const pgStorageUsedPoints = useMemo(() => {
    if (!liveData) return [];
    if (useLive) return liveData.postgres.storageUsedGbSeries ?? [];
    return toSeries(labels, storageUsedGb);
  }, [liveData, useLive, labels, storageUsedGb]);

  const pgStorageFreePoints = useMemo(() => {
    if (!liveData) return [];
    if (useLive) return liveData.postgres.storageFreeGbSeries ?? [];
    return labels.map((label, i) => ({
      label,
      value: Math.max(0, demoTotalStorageGb - (storageUsedGb[i] ?? 0)),
    }));
  }, [liveData, useLive, labels, storageUsedGb]);

  const totalStorageGb = useMemo(() => {
    if (useLive && liveData?.postgres.storageTotalGb != null && liveData.postgres.storageTotalGb > 0) {
      return liveData.postgres.storageTotalGb;
    }
    if (useDemo) return demoTotalStorageGb;
    return 1;
  }, [useLive, useDemo, liveData]);

  const latestStorageUsed = useMemo(() => {
    if (useLive && liveData) {
      const p = liveData.postgres;
      if (p.storageUsedGb != null) return p.storageUsedGb;
      if (p.storageUsedPercent != null && totalStorageGb > 0) {
        return (p.storageUsedPercent / 100) * totalStorageGb;
      }
    }
    if (useDemo && storageUsedGb.length) return storageUsedGb[storageUsedGb.length - 1] ?? 0;
    return 0;
  }, [useLive, useDemo, liveData, totalStorageGb, storageUsedGb]);

  const latestStorageFree = useMemo(() => {
    if (useLive && liveData?.postgres.storageFreeGb != null) {
      return clamp(liveData.postgres.storageFreeGb, 0, totalStorageGb);
    }
    if (useDemo) return clamp(totalStorageGb - latestStorageUsed, 0, totalStorageGb);
    return 0;
  }, [useLive, useDemo, liveData, totalStorageGb, latestStorageUsed]);

  const databaseSizeEntries = useMemo(() => {
    if (useLive && liveData?.postgres.databaseSizes?.length) {
      return liveData.postgres.databaseSizes;
    }
    if (useDemo) {
      const primaryBytes = Math.max(0, latestStorageUsed * 1_000_000_000);
      const secondaryBytes = Math.max(0, latestStorageFree * 0.18 * 1_000_000_000);
      return [
        { databaseName: 'pulse_staging_db', sizeBytes: primaryBytes },
        { databaseName: 'pulse_itsm_staging', sizeBytes: secondaryBytes },
        { databaseName: 'azure_sys', sizeBytes: 8.38 * 1_000_000 },
        { databaseName: 'postgres', sizeBytes: 8.18 * 1_000_000 },
        { databaseName: 'azure_maintenance', sizeBytes: 7.95 * 1_000_000 },
        { databaseName: 'template1', sizeBytes: 7.73 * 1_000_000 },
        { databaseName: 'template0', sizeBytes: 7.73 * 1_000_000 },
      ].filter((entry) => entry.sizeBytes > 0);
    }
    return [];
  }, [useLive, useDemo, liveData, latestStorageUsed, latestStorageFree]);

  const fetchedLabel = liveData?.fetchedAt
    ? new Date(liveData.fetchedAt).toLocaleString()
    : '';

  const refreshDescription = useMemo(() => {
    if (refreshIntervalId === 'off') {
      return 'Live metrics auto-refresh is off. Use Refresh to update.';
    }
    if (refreshIntervalId === 'auto') {
      const min = livePollIntervalMs / 60_000;
      const label = Math.abs(min % 1) < 0.01 ? String(Math.round(min)) : min.toFixed(1);
      return `Auto refresh about every ${label} minute(s), from Monitoring configuration. Manual refresh always updates.`;
    }
    if (typeof liveMetricsRefetchInterval === 'number') {
      const s = liveMetricsRefetchInterval / 1000;
      if (s < 60) return `Charts refresh about every ${Math.round(s)} seconds.`;
      return `Charts refresh about every ${Math.round(s / 60)} minutes.`;
    }
    return '';
  }, [refreshIntervalId, liveMetricsRefetchInterval, livePollIntervalMs]);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">PostgreSQL + Application Monitoring</h1>
        <p className="text-sm text-gray-600 mt-1">
          Operational view of database (Azure PostgreSQL flexible server) and application metrics (Application Insights
          custom metrics via Log Analytics).
        </p>
        <p className="text-xs text-gray-500 mt-2">
          {isPending && 'Loading monitoring snapshot…'}
          {!isPending && useLive && (
            <>
              Live data · last updated {fetchedLabel}
              {isFetching ? ' · refreshing…' : ''}
              {' · '}
              {refreshDescription}
            </>
          )}
          {!isPending && !useLive && liveData !== undefined && (
            <>
              {useDemo && (
                <>
                  Demo sample data. Set MONITORING_LIVE_AZURE_ENABLED=true and Azure credentials on the API host for live
                  metrics.{' '}
                </>
              )}
              {refreshDescription}
            </>
          )}
        </p>

        <div className="mt-3 flex w-full flex-nowrap items-center justify-start gap-2.5 overflow-x-auto pb-1">
          {/* Time range (~30% larger touch targets & type) */}
          <div
            className="inline-flex shrink-0 items-stretch rounded-md border border-gray-200 bg-white shadow-sm divide-x divide-gray-200"
            role="group"
            aria-label="Time range"
          >
            <button
              type="button"
              disabled={isPending || !canTimeShiftBack}
              onClick={() => shiftTimeRange(-1)}
              className="inline-flex items-center justify-center px-2.5 py-2 text-gray-500 hover:bg-primary-50 hover:text-primary-700 disabled:opacity-30 disabled:pointer-events-none transition-colors"
              aria-label="Shorter time range"
            >
              <ChevronDoubleLeftIcon className="h-5 w-5" />
            </button>
            <div className="relative flex min-w-[12.5rem] max-w-[14.5rem] items-center gap-1.5 px-2 sm:min-w-[13.5rem]">
              <ClockIcon className="pointer-events-none h-5 w-5 shrink-0 text-primary-600" aria-hidden />
              <select
                id="monitoring-time-range"
                value={timeRangeId}
                onChange={(e) => setTimeRangeId(e.target.value as TimeRangeId)}
                disabled={isPending}
                className="w-full cursor-pointer appearance-none bg-white py-2 pl-1 pr-7 text-sm font-medium text-gray-800 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-sm disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Time period"
              >
                {TIME_RANGES.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.displayLabel}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            </div>
            <button
              type="button"
              disabled={isPending || !canTimeShiftForward}
              onClick={() => shiftTimeRange(1)}
              className="inline-flex items-center justify-center px-2.5 py-2 text-gray-500 hover:bg-primary-50 hover:text-primary-700 disabled:opacity-30 disabled:pointer-events-none transition-colors"
              aria-label="Longer time range"
            >
              <ChevronDoubleRightIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              disabled={isPending || !canTimeShiftForward}
              onClick={zoomTimeRangeOut}
              className="inline-flex items-center justify-center px-2.5 py-2 text-gray-500 hover:bg-primary-50 hover:text-primary-700 disabled:opacity-30 disabled:pointer-events-none transition-colors"
              aria-label="Zoom out (wider range)"
            >
              <MagnifyingGlassMinusIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Refresh */}
          <div
            className="inline-flex shrink-0 items-stretch rounded-md border border-gray-200 bg-white shadow-sm divide-x divide-gray-200"
            role="group"
            aria-label="Refresh"
          >
            <button
              type="button"
              disabled={isPending}
              onClick={() => void refetch()}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50 disabled:opacity-45 disabled:pointer-events-none transition-colors"
            >
              <ArrowPathIcon className={`h-5 w-5 shrink-0 ${isFetching ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <div className="relative flex items-center">
              <select
                id="monitoring-refresh"
                value={refreshIntervalId}
                onChange={(e) => setRefreshIntervalId(e.target.value as RefreshIntervalId)}
                disabled={isPending}
                className="min-w-[4.25rem] cursor-pointer appearance-none bg-white py-2 pl-2.5 pr-8 text-sm font-medium text-gray-800 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Auto refresh interval"
              >
                {REFRESH_OPTIONS.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            </div>
          </div>
        </div>
      </div>

      {isError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          Could not load monitoring snapshot: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      )}

      {liveData?.errors && liveData.errors.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <p className="font-medium">Some live data sources failed</p>
          <ul className="mt-2 list-disc list-inside space-y-1">
            {liveData.errors.map((e) => (
              <li key={`${e.source}-${e.message.slice(0, 80)}`}>
                <span className="font-mono text-xs">{e.source}</span>: {e.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {useLive && liveData?.setupHints && liveData.setupHints.length > 0 && (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800">
          <p className="font-medium text-slate-900">Configuration checklist</p>
          <ul className="mt-2 list-disc list-inside space-y-1.5">
            {liveData.setupHints.map((hint, i) => (
              <li key={i}>{hint}</li>
            ))}
          </ul>
          {liveData.applicationInsightsPublishing === false && (
            <p className="mt-2 text-xs text-amber-800">
              Application Insights publishing is off — CPU/memory/network and custom <code className="font-mono">app.*</code>{' '}
              charts may stay empty. Request rate can still use <code className="font-mono">AppRequests</code> when telemetry
              exists in the workspace.
            </p>
          )}
        </div>
      )}

      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-800">PostgreSQL</h2>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <LineCard
            title="PostgreSQL Database CPU Percent"
            points={pgCpuPoints}
            color="rgb(124, 58, 237)"
            loading={awaitingSnapshot}
          />
          <LineCard
            title="PostgreSQL Database Memory Percent"
            points={pgMemoryPoints}
            color="rgb(59, 130, 246)"
            loading={awaitingSnapshot}
          />
        </div>
        <PostgresStorageDualLineCard
          usedPoints={pgStorageUsedPoints}
          freePoints={pgStorageFreePoints}
          loading={awaitingSnapshot}
        />
        <DatabaseSizePieCard entries={databaseSizeEntries} loading={awaitingSnapshot} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <GaugeCard
            title="PostgreSQL Storage Used"
            value={latestStorageUsed}
            total={totalStorageGb}
            unit=" GB"
            loading={awaitingSnapshot}
          />
          <GaugeCard
            title="PostgreSQL Storage Free"
            value={latestStorageFree}
            total={totalStorageGb}
            unit=" GB"
            reverse
            loading={awaitingSnapshot}
          />
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-800">Application</h2>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <LineCard title="Application CPU Load" points={appCpuPoints} color="rgb(239, 68, 68)" loading={awaitingSnapshot} />
          <LineCard
            title="Application Memory Usage"
            points={appMemoryPoints}
            color="rgb(16, 185, 129)"
            loading={awaitingSnapshot}
          />
          <LineCard title="Network Load" points={networkLoadPoints} color="rgb(245, 158, 11)" loading={awaitingSnapshot} />
          <LineCard
            title="Application Load (HTTP Requests/sec)"
            points={appLoadPoints}
            color="rgb(99, 102, 241)"
            unit=" req/s"
            loading={awaitingSnapshot}
          />
        </div>
      </section>
    </div>
  );
};

export default MonitoringDashboard;
